import { defineStore } from 'pinia';
import { readCookie } from '@/modules/cookies';
import { useTestModeStore } from '@/stores/testMode';

export interface CollectState {
  uploaded: boolean;
  lastUploadDate: string | null;
  queueStartedAt: string;
  track: number;
  sequences: Record<string, number>;
  globalSequence: number;
  recordsInQueue: number;
  serverIsAvailable: boolean;
  engagementKey: string | null;
}

export const defaultState = (): CollectState => ({
  uploaded: false,
  lastUploadDate: null,
  queueStartedAt: new Date().toISOString(),
  track: 0,
  sequences: {},
  globalSequence: 1,
  recordsInQueue: 0,
  serverIsAvailable: false,
  engagementKey: null,
});

export const useCollectStore = defineStore('collect', {
  persist: true,
  state: defaultState,

  getters: {
    // test mode allows the same encounter to be uploaded more than once
    ready(state): boolean {
      return useTestModeStore().get('multipleUploadsOfData') || !state.uploaded;
    },
  },

  actions: {
    clearUploaded(): void {
      this.uploaded = false;
    },

    resetQueue(): void {
      this.queueStartedAt = new Date().toISOString();
      this.track = 0;
    },

    setLastUploadDate(): void {
      this.lastUploadDate = new Date().toISOString();
    },

    incrementTrackCounter(): void {
      this.track = (this.track || 0) + 1;
    },

    setRecordsInQueue(count: number): void {
      this.recordsInQueue = count || 0;
    },

    incrementRecordsInQueue(): void {
      this.recordsInQueue = (this.recordsInQueue || 0) + 1;
    },

    incrementSequenceCounter(name: string): void {
      this.globalSequence = this.globalSequence + 1;
      this.sequences[name] = (this.sequences[name] || 1) + 1;
    },

    async checkIfServerIsAvailable(): Promise<boolean> {
      let results = false;
      try {
        const response = await fetch('api/v3/device/ready');
        results = response.ok;
      }
      catch (e) {
        console.log(e);
      }

      this.serverIsAvailable = results;
      return results;
    },

    // vuex bucketed this per session cookie; pinia persists under one key
    syncEngagement(): void {
      const key = readCookie('session');
      if (this.engagementKey === key) return;

      this.$reset();
      this.$patch({ engagementKey: key });
    },
  },
})
